// @ts-nocheck
/**
 * One place for app-shell analytics events.
 *
 *   vhpTrack('recipe_share', { recipe_url, method })
 *
 * Each event goes to GA4 (gtag, as before) and to Shopify customer events
 * (Shopify.analytics.publish('vhp_<name>', params), readable by web pixels).
 * Nothing is sent before the visitor answered the cookie banner and allowed analytics: events wait in
 * memory (max 60) and are flushed once consent is given, or dropped when it is refused.
 *
 * Declarative clicks: <a data-track="planner_cta_click" data-track-source="topbar"> sends
 * { source: 'topbar' } on click.
 *
 * Event overview (name: params)
 *   feed_swipe: direction right|left, slot, n, surface        every swipe in the deck
 *   reel_like / reel_skip: recipe_url, slot                    like / dislike
 *   recipe_sheet_open: recipe_url                              bottom sheet opened
 *   recipe_cook_mode: recipe_url                               cooking mode started
 *   recipe_share: recipe_url, method native|copy               share tapped
 *   recipe_plan_add: source sheet|cook, subscriber, plannable  "Zum Meal Plan hinzufügen"
 *   plan_paywall_shown: source                                 soft paywall for non-subscribers
 *   planner_cta_click: source, ...                             any link/button into the planner offer (also kept in
 *                                                              sessionStorage for the planner's planner_entry step)
 *   plan_explainer_open: source top|end                        recipe CTA / card opened the planner explainer (its
 *                                                              CTA is the planner_cta_click, assets/vhp-plan-goal.js)
 *   save_likes_prompt_shown / _click / _converted: source      "Speicher deine Likes"
 *   feed_native_shown / _dismiss / _click: card                intro, planner, coaching cards (intro_video: the
 *                                                              dismiss also carries sec watched and done)
 *   feed_intro_progress: p 25|50|75|100                         Sören's intro video in the deck, watched share
 *   feed_intro_sound: on, sec                                   its sound button
 *   explore_search: q, results, source                         searches in the site search (results 0 = content gap)
 *   search_ingredients: ings (tofu+brokkoli), results          ingredient search / combination
 *   explore_category_click: handle                              story circle -> swipe deck
 *   explore_quick_filter: filter / explore_sort: sort          Explore quick chips and sort
 *   feed_mode, feed_filter, explore_result_click, explore_open_feed
 *   tab_switch: from, to, cached                              bottom tab switched in-page (assets/app-router.js)
 */
(() => {
  'use strict';

  if (window.vhpTrack) return;

  const QUEUE_MAX = 60;
  const queue = [];

  const privacy = () => window.Shopify?.customerPrivacy || null;

  const consent = () => {
    const api = privacy();
    if (!api) return 'pending';
    try {
      if (api.analyticsProcessingAllowed()) return 'yes';
      const current = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
      return current && current.analytics === '' ? 'pending' : 'no';
    } catch (e) {
      return 'pending';
    }
  };

  const clean = (params) => {
    const out = {};
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (typeof value === 'string') out[key] = value.slice(0, 120);
      else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
      else if (typeof value === 'boolean') out[key] = value;
    });
    return out;
  };

  const deliver = (name, params) => {
    try {
      if (typeof gtag === 'function') gtag('event', name, params);
    } catch (e) {
      /* analytics must never break the page */
    }
    try {
      window.Shopify?.analytics?.publish?.(`vhp_${name}`, params);
    } catch (e) {
      /* optional */
    }
  };

  const flush = () => {
    if (!queue.length) return;
    const state = consent();
    if (state === 'pending') return;
    const items = queue.splice(0, queue.length);
    if (state !== 'yes') return;
    items.forEach((item) => deliver(item.name, item.params));
  };

  // Which button led into the planner, for its anonymous planner_entry step (assets/vhp-planner-events.js). Kept
  // for this tab only, no id, independent of the analytics consent (it never leaves the browser by itself).
  const ENTRY_KEY = 'vhp_planner_entry';
  const rememberEntry = (source) => {
    try {
      sessionStorage.setItem(ENTRY_KEY, JSON.stringify({ s: String(source || ''), p: pageType, at: Date.now() }));
    } catch (e) {
      /* private mode */
    }
  };

  // Page type of the page in front of the visitor (docs/entry.md in vhp-planner): for page_seen and the page an
  // entry was tapped on (entry_page). Read from the page URL, and from the tab URL after an in-page tab switch
  // (assets/app-router.js), so a recipe sheet on top of the feed still counts as the feed. null = the planner itself.
  const typeOf = (href) => {
    let path = '/';
    try {
      path = new URL(href, location.href).pathname;
    } catch (e) {
      return 'other';
    }
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    if (root && (path === root || path.startsWith(`${root}/`))) path = path.slice(root.length) || '/';
    path = path.replace(/\/+$/, '') || '/';
    if (path === '/pages/meal-planner') return null;
    if (path === '/') return 'home';
    if (path.startsWith('/collections/')) return path.includes('/products/') ? 'recipe' : 'collection';
    if (path.startsWith('/products/') || /^\/blogs\/rezepte\/[^/]+$/.test(path)) return 'recipe';
    if (path === '/blogs/rezepte' || path === '/search') return 'explore';
    if (path === '/pages/wunschliste') return 'likes';
    if (path === '/pages/profil') return 'profile';
    if (path === '/pages/plan') return 'plan';
    return 'other';
  };
  let pageType = typeOf(location.href);
  window.addEventListener('vhp:view-show', (event) => {
    pageType = typeOf(event.detail?.url || location.href);
    document.dispatchEvent(new CustomEvent('vhp:page-type', { detail: { type: pageType } }));
  });

  const track = (rawName, params) => {
    const name = String(rawName || '').replace(/[^a-z0-9_]/gi, '_').slice(0, 40);
    if (!name) return;
    const data = clean(params);
    if (name === 'planner_cta_click') rememberEntry(data.source);
    // "Plan ansehen" on "Dein Plan ist fertig" in the feed.
    else if (name === 'reel_plan_cta') rememberEntry('feed_ready');
    const state = consent();
    if (state === 'yes') deliver(name, data);
    else if (state === 'pending') {
      queue.push({ name, params: data });
      if (queue.length > QUEUE_MAX) queue.splice(0, queue.length - QUEUE_MAX);
    }
  };

  window.vhpTrack = track;
  track.pageType = () => pageType;

  // Pick up the consent API and flush when the banner is answered.
  try {
    if (!privacy() && typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) flush();
      });
    }
  } catch (e) {
    /* no consent API: nothing is sent */
  }
  document.addEventListener('visitorConsentCollected', flush);
  window.addEventListener('pagehide', flush);
  [1500, 5000, 15000].forEach((ms) => window.setTimeout(flush, ms));

  document.addEventListener(
    'click',
    (event) => {
      const el = event.target instanceof Element ? event.target.closest('[data-track]') : null;
      if (!el) return;
      const params = {};
      Object.keys(el.dataset).forEach((key) => {
        if (key.startsWith('track') && key !== 'track') {
          const name = key.slice(5);
          params[name.charAt(0).toLowerCase() + name.slice(1)] = el.dataset[key];
        }
      });
      track(el.dataset.track, params);
    },
    true
  );
})();
